import React, { useContext } from "react";
import { Box } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import { mostOrderedNearby } from "../../apollo/server";
import ConfigurationContext from "../../context/Configuration";
import { resizedImage } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";
import { ROW_SX, SectionHead, useRowScroller } from "./listUi";

const MOST_ORDERED_NEARBY = gql`${mostOrderedNearby}`;

function DishCard({ dish, rank, currency, onOpen }) {
  const price = dish.price != null ? `${dish.priceIsFrom ? "From " : ""}${currency}${Number(dish.price).toFixed(2)}` : null;
  const open = () => onOpen(dish);
  return (
    <Box component="li" sx={{ position: "relative", flex: "0 0 240px", scrollSnapAlign: "start" }}>
      <Box sx={{ position: "relative", aspectRatio: "4 / 3", borderRadius: "16px", overflow: "hidden", bgcolor: C.surface2 }}>
        {dish.image ? (
          <Box component="img" src={resizedImage(dish.image, 480)} alt="" loading="lazy" decoding="async" sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
            <RestaurantRoundedIcon aria-hidden="true" sx={{ fontSize: 36 }} />
          </Box>
        )}
        <Box component="span" sx={{ position: "absolute", top: 10, left: 10, height: 26, px: 1, display: "inline-flex", alignItems: "center", borderRadius: "999px", bgcolor: C.bg, color: C.red, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 800, boxShadow: "0 1px 3px rgba(26,25,24,0.12)" }}>
          #{rank}
        </Box>
        {dish.isAiImage && (
          <Box component="span" sx={{ position: "absolute", left: 10, bottom: 10, px: 0.75, height: 20, display: "inline-flex", alignItems: "center", borderRadius: "6px", bgcolor: "rgba(26,25,24,0.72)", color: "#FFFFFF", fontFamily: FONT_BODY, fontSize: 11, fontWeight: 600 }}>
            AI image
          </Box>
        )}
      </Box>
      {/* Above the stretched link. */}
      <Box
        component="button"
        type="button"
        onClick={open}
        aria-label={`Add ${dish.title} from ${dish.restaurantName}`}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          position: "absolute",
          zIndex: 1,
          // Bottom-right of the 4:3 photo (240 × 180).
          top: 180 - 44 - 10,
          right: 10,
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: C.red,
          color: "#FFFFFF",
          border: "2px solid #FFFFFF",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(26,25,24,0.25)",
          "&:hover": { bgcolor: C.redHover },
        }}
      >
        <AddRoundedIcon aria-hidden="true" sx={{ fontSize: 24 }} />
      </Box>
      <Box sx={{ pt: 1.25 }}>
        <Box
          component="button"
          type="button"
          data-row-item
          onClick={open}
          sx={{
            ...BUTTON_RESET,
            display: "block",
            width: "100%",
            textAlign: "left",
            fontFamily: FONT_BODY,
            fontSize: 16,
            fontWeight: 700,
            color: C.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "16px" },
            "&:focus-visible": { outline: "none" },
            "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: "3px" },
          }}
        >
          {dish.title}
        </Box>
        <Box sx={{ fontFamily: FONT_BODY, fontSize: 13, color: C.muted, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {dish.restaurantName}
          {price ? ` · ${price}` : ""}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * "Most ordered near you": the dishes ordered most across `restaurantIds`
 * (the restaurants on screen). Nothing renders until there are some.
 */
function MostOrderedNearby({ restaurantIds, city, onOpenDish }) {
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const row = useRowScroller();
  const { data } = useQuery(MOST_ORDERED_NEARBY, {
    variables: { restaurantIds, limit: 12 },
    skip: !restaurantIds.length,
    fetchPolicy: "cache-first",
  });
  const dishes = (data?.mostOrderedNearby || []).filter((d) => d.restaurantSlug);
  if (!dishes.length) return null;
  return (
    <Box component="section" aria-labelledby="near-title">
      <SectionHead
        id="near-title"
        title="Most ordered near you"
        sub={city ? `The dishes ${city} keeps coming back for` : "The dishes your neighbours keep coming back for"}
        row={row}
        rowLabel="dishes"
      />
      <Box component="ul" ref={row.ref} onKeyDown={row.onKeyDown} aria-labelledby="near-title" sx={{ ...ROW_SX, gap: 2 }}>
        {dishes.map((dish, i) => (
          <DishCard key={`${dish.restaurantId}-${dish._id}`} dish={dish} rank={i + 1} currency={currency} onOpen={onOpenDish} />
        ))}
      </Box>
    </Box>
  );
}

export default React.memo(MostOrderedNearby);
